import { object } from "../helpers/object";
import { debug } from "../helpers/debug";
import { utils } from "../helpers/utils";
import { responsive } from "../helpers/responsive";

import Glide from "@glidejs/glide";
import tailwindConfigSpacing from "../../../tailwind.spacing.config.js";

/*------------------------------------*\
  #SLIDER

  Class gestion des element [data-js-component="slider"]

  Permet la gestion d'un slider fonctionnel 
  uniquement en mobile [data-glide-enable="true"]

  [data-js-component="slider"]
  [data-glide-per-view-sm="{int}"] // nombre de slide visible en mobile
  [data-glide-per-view-md="{int}"] // nom de slide visible en tablet
  [data-glide-per-view-lg="{int}"] // nombre de slide visible en desktop
  [data-glide-overflow="{bool}"] // slider en overflow (true -> dépasse de son container)
  [data-glide-mobile-only="{bool}"] // slider fonctionnel uniquement en mobile
  [data-glide-enable="{bool}"] // slider actif (géré en js)
\*------------------------------------*/
class Slider {

  sliders;

  constructor(module){
    this.MODULE = module;
    this.DOM_MODULE = object.toArray(document.querySelectorAll(this.MODULE));
    this.sliders = [];
    this.sliderID = 0;
  }
  /**
   * INITIALISATION
   * @returns 
   */
  init = () => {
    if (!this.DOM_MODULE.length) return;
    // INIT SLIDERS
    this.glideInit();
    // INIT RESPONSIVE
    this.responsiveHandler();
    // KEY EVENTS
    document.addEventListener('keyup', this.keyHandler);

    if(debug.DEBUG) console.log('components/slider.js');
  }
  /**
   * Refreshes the state of all sliders based on the current screen size and slider settings.
   * 
   * This function iterates over each slider and performs the following actions:
   * - Checks if the slider is mobile-only and whether the current screen size is large.
   * - Enables or disables the slider based on the screen size and slider settings.
   * - Updates the `data-glide-enable` attribute of the slider element.
   * - Adjusts the visibility of bullets for sliders of type 'slider'.
   * - Sets the CSS variable `--slider-column-count` to the number of slides per view.
   * - Resets the slider position based on the number of slides and the `perView` setting.
   * 
   * @returns {void}
   */
  slidersRefresh = () => {
    this.sliders.forEach((slider, index) => {
      const isMobileOnly = slider.mobileOnly;
      const isLargeScreen = responsive.is('lg');
      const shouldEnableSlider = isMobileOnly && !isLargeScreen;
      const hasGlideInstance = slider.glide !== null;
  
      if (isMobileOnly) {
        if (shouldEnableSlider && !hasGlideInstance) {
          slider = this.glideCreate(slider.element);
          slider.glide.enable();
          slider.element.setAttribute('data-glide-enable', true);
        } else if (!shouldEnableSlider && hasGlideInstance) {
          slider.glide.disable();
          slider.glide.destroy();
          slider.glide = null;
          slider.element.setAttribute('data-glide-enable', false);
        } else if (!hasGlideInstance) {
          return;
        }
      } else {
        slider.element.setAttribute('data-glide-enable', slider.slidesCount > parseInt(slider.glide.settings.perView));
      }
  
      if (slider.type === 'slider') {
        const perView = slider.glide.settings.perView;
        const bullets = Array.from(slider.element.querySelectorAll('.glide__bullet'));
        const bulletsVisible = (slider.slidesCount - perView) + 1;
  
        bullets.forEach((bullet, index) => {
          bullet.setAttribute('aria-disabled', index >= bulletsVisible || bulletsVisible === 1);
        });
      }
  
      slider.element.style.setProperty('--slider-column-count', slider.glide.settings.perView);
  
      if (slider.slidesCount > parseInt(slider.glide.settings.perView)) {
        slider.glide.go('=0');
      } else {
        slider.glide.go('>');
      }
  
      this.sliders[index] = slider;
    });
  }
  
  /**
   * KEY HANDLER
   * 
   * navigation au clavier
   * @param {event} event 
   */
  keyHandler = (event) => {
    if ((utils.isTabKey(event.key || event.keyCode) || utils.isSpaceKey(event.key || event.keyCode) ) 
      && event.target.closest('.glide__slide')) {
      event.preventDefault();
      event.stopPropagation();
      const slideID = event.target.closest('.glide__slide').getAttribute('data-glide-id');
      const slider = event.target.closest('.glide');
      const sliderID = slider.id;
      const sliderEnabled = slider.getAttribute('data-glide-enable') === 'true';
      if (!sliderEnabled) return;
      const currentSlider = this.sliders.find(slider => slider.id === sliderID);
      if (currentSlider) {
        currentSlider.glide.go(`=${slideID}`);
      }
    }
  };
  /**
   * RESPONSIVE
   */
  responsiveHandler = () => {
    document.addEventListener('RESPONSIVE_HAS_CHANGED', (event) => {
      this.slidersRefresh();
    })
  };
  /**
   * Initializes a Glide.js slider with the specified configuration.
   *
   * @param {HTMLElement} slider - The slider element to initialize.
   * @returns {Object} An object containing the slider type, element, Glide instance, 
   *                   mobileOnly flag, and the number of slides.
   *
   * @property {string} type - The type of the slider ('slider' or 'carousel').
   * @property {HTMLElement} element - The slider element.
   * @property {Glide|null} glide - The Glide instance or null if not initialized.
   * @property {boolean} mobileOnly - Flag indicating if the slider is mobile-only.
   * @property {number} slidesCount - The number of slides in the slider.
   */
  glideCreate = (slider) => {
    const getData = (attr, fallback) => parseFloat(slider.dataset[attr]) || fallback;
    const PerViewSM = getData('glidePerViewSm', 1);
    const PerViewMD = getData('glidePerViewMd', 2);
    const PerViewLG = getData('glidePerViewLg', 4);
    const gap = slider.dataset.glideGap || tailwindConfigSpacing.gutter;
    const type = slider.dataset.glideType || 'slider';
    const focusAt = type === 'carousel' ? 'center' : 0;
    const mobileOnly = utils.dataBool(slider.dataset.glideMobileOnly) || false;
    const slides = slider.querySelectorAll('.glide__slide');
    const bulletsContainer = slider.querySelector('.js-glide-bullets');
    const slidesCount = slides.length;
  
    slider.style.setProperty('--slider-gap', gap);
    slider.id = `slider-${this.sliderID++}`;
  
    if (mobileOnly && responsive.is('lg')) {
      slider.setAttribute('data-glide-enable', false);
      return { type, element: slider, glide: null, mobileOnly, slidesCount };
    }
  
    if (bulletsContainer) {
      bulletsContainer.innerHTML = '';
      slides.forEach((_, i) => {
        const button = document.createElement('button');
        const span = document.createElement('span');
        span.textContent = `Voir la slide ${i + 1}`;
        span.classList.add('o-hidden-visually');
        button.appendChild(span);
        button.setAttribute('aria-hidden', 'true');
        button.setAttribute('tabindex', '-1');
        button.classList.add('glide__bullet');
        button.dataset.glideDir = `=${i}`;
        bulletsContainer.appendChild(button);
      });
    }
  
    const glide = new Glide(slider, {
      type,
      perView: PerViewLG,
      bound: true,
      rewind: true,
      gap,
      focusAt,
      breakpoints: {
        767: { perView: PerViewSM },
        1023: { perView: PerViewMD }
      }
    }).mount();
  
    if (slidesCount === glide.settings.perView) {
      slider.setAttribute('data-glide-enable', false);
    }
  
    return { type, element: slider, glide, mobileOnly, slidesCount, 'id': slider.id};
  };

  /**
   * GET SLIDER
   * Returns the slider object at the specified index
   * @param {number} index - The index of the slider to retrieve
   * @returns {object|undefined} The slider object or undefined if not found
   */
  get = (index) => {
    return this.sliders[index];
  };
  
  /**
   * GLIDE INIT
   * 
   * initialisation de la library Glide.js
   */
  glideInit = () => {
    this.DOM_MODULE.forEach((slider) => {
      this.sliders.push(this.glideCreate(slider));
    })
  }
  add = (slider) => {
    this.sliders.push(this.glideCreate(slider));
    this.slidersRefresh();
  }
}
export const slider = new Slider('[data-js-component="slider"]'); 